import React, { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { FileText, Boxes, Wallet, PenSquare, ScrollText, ShieldAlert, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

// Reuso das páginas já existentes — renderizadas dentro de cada aba
import TemplatesProposta from "./TemplatesProposta";
import ConfiguracoesModelos from "./ConfiguracoesModelos";
import CatalogoEquipamentos from "./CatalogoEquipamentos";
import CondicoesPagamento from "./CondicoesPagamento";
import ConfiguracoesEmpresa from "./ConfiguracoesEmpresa";

// ============================================================
// SEÇÃO 5 — Assinaturas padrão (signatário da empresa nos contratos)
// ============================================================
const AssinaturasPadrao = ({ canEdit }) => {
  const [unidades, setUnidades] = useState([]);
  const [selUni, setSelUni] = useState("global");
  const [form, setForm] = useState({
    representante_nome: "",
    representante_cargo: "",
    representante_cpf: "",
    representante_email: "",
    cidade: "",
    observacoes: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/comercial/unidades");
        setUnidades(Array.isArray(data) ? data : data?.items || []);
      } catch {
        setUnidades([]);
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/configuracoes/assinaturas-padrao/${selUni}`)
      .then(({ data }) => {
        setForm({
          representante_nome: data.representante_nome || "",
          representante_cargo: data.representante_cargo || "",
          representante_cpf: data.representante_cpf || "",
          representante_email: data.representante_email || "",
          cidade: data.cidade || "",
          observacoes: data.observacoes || "",
        });
      })
      .catch(() => {
        toast.error("Falha ao carregar configuração de assinaturas");
      })
      .finally(() => setLoading(false));
  }, [selUni]);

  const handleSave = async () => {
    if (!canEdit) return;
    if (!form.representante_nome || form.representante_nome.trim().length < 3) {
      toast.error("Informe o nome do representante (mínimo 3 caracteres)");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/configuracoes/assinaturas-padrao/${selUni}`, form);
      toast.success("Assinatura padrão salva");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 space-y-5" data-testid="config-assinaturas-padrao">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold">Assinatura padrão do contrato</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Dados do representante da Locar Ativo Brasil que assina os contratos gerados.
            Esses valores preenchem as variáveis <code>{"{{representante_nome}}"}</code>,{" "}
            <code>{"{{representante_cargo}}"}</code> e <code>{"{{cidade}}"}</code> do modelo.
          </p>
        </div>
        <div className="min-w-[220px]">
          <Label className="text-xs">Aplicar a</Label>
          <select
            data-testid="assinatura-unidade-select"
            value={selUni}
            onChange={(e) => setSelUni(e.target.value)}
            className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
          >
            <option value="global">Padrão global (todas)</option>
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome} {u.is_matriz ? "★" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!canEdit && (
        <div className="flex gap-2 items-start p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs">
          <ShieldAlert size={16} className="shrink-0 mt-0.5" />
          <span>
            Somente Gerente, Supervisão e Diretor podem editar estas configurações. Você está em modo leitura.
          </span>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          <Loader2 className="inline animate-spin mr-2" size={14} /> Carregando…
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Nome do representante *</Label>
            <Input
              data-testid="assinatura-nome"
              value={form.representante_nome}
              onChange={(e) => setForm({ ...form, representante_nome: e.target.value })}
              disabled={!canEdit}
              placeholder="Ex.: João Silva"
            />
          </div>
          <div>
            <Label>Cargo</Label>
            <Input
              data-testid="assinatura-cargo"
              value={form.representante_cargo}
              onChange={(e) => setForm({ ...form, representante_cargo: e.target.value })}
              disabled={!canEdit}
              placeholder="Ex.: Diretor Comercial"
            />
          </div>
          <div>
            <Label>CPF</Label>
            <Input
              data-testid="assinatura-cpf"
              value={form.representante_cpf}
              onChange={(e) => setForm({ ...form, representante_cpf: e.target.value })}
              disabled={!canEdit}
              placeholder="000.000.000-00"
            />
          </div>
          <div>
            <Label>E-mail</Label>
            <Input
              data-testid="assinatura-email"
              type="email"
              value={form.representante_email}
              onChange={(e) => setForm({ ...form, representante_email: e.target.value })}
              disabled={!canEdit}
              placeholder="contratos@locarativosbrasil.com.br"
            />
          </div>
          <div>
            <Label>Cidade da assinatura</Label>
            <Input
              data-testid="assinatura-cidade"
              value={form.cidade}
              onChange={(e) => setForm({ ...form, cidade: e.target.value })}
              disabled={!canEdit}
              placeholder="Recife"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Observações internas</Label>
            <Textarea
              data-testid="assinatura-observacoes"
              value={form.observacoes}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
              disabled={!canEdit}
              rows={2}
            />
          </div>
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end">
          <Button
            data-testid="assinatura-salvar"
            onClick={handleSave}
            disabled={saving || loading}
            style={{ background: "#1B3A6B", color: "white" }}
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Salvar
          </Button>
        </div>
      )}
    </Card>
  );
};

// ============================================================
// WRAPPER — somente leitura: bloqueia interações de edição
// ============================================================
const ReadOnlyOverlay = ({ children, canEdit }) => {
  if (canEdit) return children;
  return (
    <div data-testid="config-readonly-mode" className="relative">
      <div className="mb-3 flex gap-2 items-center p-3 rounded-md bg-slate-50 border text-slate-700 text-xs">
        <ShieldAlert size={14} />
        <span>Modo somente leitura — Gerente/Diretor têm permissão de edição.</span>
      </div>
      <div className="pointer-events-none opacity-90">{children}</div>
    </div>
  );
};

// ============================================================
// PÁGINA PRINCIPAL — 5 abas
// ============================================================
const TABS = [
  { v: "empresa", l: "Dados da Empresa", Icon: Building2 },
  { v: "modelos-proposta", l: "Modelos de Proposta", Icon: FileText },
  { v: "modelos-contrato", l: "Modelos de Contrato", Icon: ScrollText },
  { v: "equipamentos", l: "Equipamentos", Icon: Boxes },
  { v: "condicoes", l: "Condições de Pagamento", Icon: Wallet },
  { v: "assinaturas", l: "Assinaturas Padrão", Icon: PenSquare },
];

export default function ConfiguracoesProposta() {
  const { user } = useAuth();
  const canEdit = ["diretor", "admin_templates"].includes(user?.role);
  const [tab, setTab] = useState("empresa");

  return (
    <div className="space-y-6" data-testid="configuracoes-proposta-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configurações de Proposta</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Centralize aqui todos os elementos que compõem uma proposta: modelos, equipamentos,
            condições comerciais e assinaturas padrão.
          </p>
        </div>
        <Badge variant="outline" className="text-xs">
          {canEdit ? "Modo edição" : "Somente leitura"}
        </Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-white border h-auto p-1 flex-wrap gap-1" data-testid="config-proposta-tabs">
          {TABS.map((t) => {
            const Icon = t.Icon;
            return (
              <TabsTrigger
                key={t.v}
                value={t.v}
                data-testid={`tab-${t.v}`}
                className="data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 gap-2"
              >
                <Icon size={14} />
                {t.l}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="empresa" className="m-0">
          <ConfiguracoesEmpresa canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="modelos-proposta" className="m-0">
          <ReadOnlyOverlay canEdit={canEdit}>
            <TemplatesProposta />
          </ReadOnlyOverlay>
        </TabsContent>

        <TabsContent value="modelos-contrato" className="m-0">
          <ReadOnlyOverlay canEdit={canEdit}>
            <ConfiguracoesModelos />
          </ReadOnlyOverlay>
        </TabsContent>

        <TabsContent value="equipamentos" className="m-0">
          <ReadOnlyOverlay canEdit={canEdit}>
            <CatalogoEquipamentos />
          </ReadOnlyOverlay>
        </TabsContent>

        <TabsContent value="condicoes" className="m-0">
          <ReadOnlyOverlay canEdit={canEdit}>
            <CondicoesPagamento />
          </ReadOnlyOverlay>
        </TabsContent>

        <TabsContent value="assinaturas" className="m-0">
          <AssinaturasPadrao canEdit={canEdit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
